import { AlertTriangle, CheckCircle2, ChevronRight, ExternalLink, RefreshCw, RotateCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getJson, loadControlRoom, postJson, type ControlRoomSnapshot } from './api';
import { loadBot, type TwitchBotSnapshot } from './bot-api';
import type { RebuildRouteId } from './navigation';
import { loadStartStreamState, type EffectsSnapshot, type ProductionSnapshot } from './stream-api';
import './workspace.css';

type WeekendSnapshot = {
  version: string;
  room: { fresh: boolean; roomUrl: string | null; lastSeenAt: string | null; staleAfterSeconds: number };
  blackYellowFallback: { soundName: string | null; enabled: boolean };
  commands: Array<{ command: string; enabled: boolean; aliases: string[] }>;
  alerts: Array<{ id: string; type: string; userName: string | null; status: string; errorMessage: string | null; createdAt: string }>;
};

type WeekendState = {
  weekend: WeekendSnapshot;
  control: ControlRoomSnapshot;
  production: ProductionSnapshot;
  effects: EffectsSnapshot;
  bot: TwitchBotSnapshot;
};

type Check = { label: string; ok: boolean; warning?: boolean; value: string; detail: string; route: RebuildRouteId };

function dateTime(value: string | null): string {
  if (!value) return 'Never';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Unknown' : parsed.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
}

function alertType(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function safeHttpUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function WeekendPrepPage() {
  const [state, setState] = useState<WeekendState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [weekend, control, stream, bot] = await Promise.all([
        getJson<WeekendSnapshot>('/api/v1/weekend-readiness'),
        loadControlRoom(),
        loadStartStreamState(),
        loadBot(),
      ]);
      setState({ weekend, control, production: stream.production, effects: stream.effects, bot });
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Weekend Prep could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const checks = useMemo<Check[]>(() => {
    if (!state) return [];
    const selectedFallback = state.effects.welcomeFallbacks.find((item) => item.style === state.effects.selectedStyle);
    const selectedStyleLabel = state.effects.styles.find((item) => item.style === state.effects.selectedStyle)?.label ?? state.effects.selectedStyle;
    const activeSession = state.effects.activeSession;
    const sessionStyleLabel = activeSession
      ? state.effects.styles.find((item) => item.style === activeSession.style)?.label ?? activeSession.style
      : null;
    const audioReady = state.production.soundboard.status === 'online' && state.production.soundboard.overlayClients > 0;
    const botReady = state.bot.enabled && state.bot.connected && state.bot.scopesReady && state.bot.broadcasterBotScope;
    return [
      { label: 'FFSControl', ok: state.control.system.status === 'online', value: state.control.system.status === 'online' ? 'ONLINE' : 'CHECK', detail: `v${state.weekend.version}`, route: 'system-health' },
      { label: 'Stream PC', ok: state.production.activeAgent?.connected === true, value: state.production.activeAgent?.connected ? 'CONNECTED' : 'OFFLINE', detail: state.production.activeAgent?.agentVersion ?? 'Agent unavailable', route: 'system-health' },
      { label: 'OBS', ok: state.production.obs.status === 'online', value: state.production.obs.status === 'online' ? 'ONLINE' : 'CHECK', detail: state.production.obs.currentProgramSceneName ?? state.production.obs.lastError ?? 'No scene reported', route: 'obs' },
      { label: 'Stream audio', ok: audioReady, warning: true, value: audioReady ? 'READY' : 'CHECK', detail: `${state.production.soundboard.overlayClients} browser client${state.production.soundboard.overlayClients === 1 ? '' : 's'}`, route: 'sounds' },
      { label: 'Text Outputs', ok: state.production.textOutputs.status === 'online', warning: true, value: state.production.textOutputs.status === 'online' ? 'READY' : 'CHECK', detail: state.production.textOutputs.lastError ?? `${state.production.textOutputs.writtenFiles} files written`, route: 'text-outputs' },
      { label: 'WhatsAMusic', ok: state.control.wam.agentConnected && state.control.wam.status === 'online', value: state.control.wam.agentConnected && state.control.wam.status === 'online' ? 'READY' : 'CHECK', detail: state.control.wam.category ?? 'No current category', route: 'wam' },
      { label: 'Chat Bot', ok: botReady, warning: true, value: botReady ? 'READY' : 'CHECK', detail: state.bot.lastError ?? state.bot.botName ?? 'FFSControl bot', route: 'chat-bot' },
      { label: 'Stream session', ok: Boolean(activeSession), warning: true, value: activeSession ? 'ACTIVE' : 'NOT STARTED', detail: activeSession ? `${sessionStyleLabel ?? selectedStyleLabel} · Welcome Cycle #${activeSession.welcomeGeneration}` : 'Start the session before going live', route: 'start-stream' },
      { label: 'Welcome fallback', ok: Boolean(selectedFallback?.configured && selectedFallback.enabled) || state.effects.selectedStyle === 'black-yellow', warning: true, value: selectedFallback?.configured && selectedFallback.enabled ? 'READY' : state.effects.selectedStyle === 'black-yellow' ? 'NOT REQUIRED' : 'CHECK', detail: selectedFallback?.soundName ?? selectedStyleLabel, route: 'welcomes' },
      { label: 'WAM room', ok: state.weekend.room.fresh, warning: true, value: state.weekend.room.fresh ? 'DETECTED' : 'OPEN ROOM', detail: state.weekend.room.fresh ? state.weekend.room.roomUrl ?? 'Detected' : 'Open the active WAM room in Chrome', route: 'wam' },
    ];
  }, [state]);

  const problems = checks.filter((item) => !item.ok);

  async function replay(id: string, label: string) {
    if (!window.confirm(`Replay ${label}?\n\nThis creates a new audited alert using the current stream session. The original Twitch event is not changed.`)) return;
    setBusy(true);
    setMessage('');
    try {
      await postJson(`/api/v1/weekend-readiness/alerts/${encodeURIComponent(id)}/replay`, {});
      setMessage(`${label} queued for replay.`);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Alert replay failed.');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !state) return <div className="ffs-next-loading">Loading Weekend Prep…</div>;

  const required = ['lurk', 'attention', 'followage', 'whats', 'nerds', 'jump'];
  const commandRows = required.map((name) => state?.weekend.commands.find((item) => item.command === name) ?? { command: name, enabled: false, aliases: [] });
  const roomUrl = state ? safeHttpUrl(state.weekend.room.roomUrl) : null;

  return (
    <div className="ffs-workspace-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      {state ? (
        <>
          <section className="ffs-workspace-hero">
            <div><small>FRIDAY / WEEKEND CHECK</small><h2>{problems.length ? `${problems.length} item${problems.length === 1 ? '' : 's'} to check` : 'Weekend stack looks ready'}</h2><p>Check the weekend stack before going live. Status cards take you to the workspace that owns each item; this page itself does not change OBS scenes.</p></div>
            <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refresh()}><RefreshCw size={15} /> Refresh</button>
          </section>

          <section className="ffs-weekend-checks" aria-label="Weekend readiness">
            {checks.map((check) => <a className={`ffs-weekend-check ${check.ok ? 'good' : check.warning ? 'warning' : 'bad'}`} key={check.label} href={`#/${check.route}`} title={`Open ${check.label} controls`}><div>{check.ok ? <CheckCircle2 size={17} /> : <AlertTriangle size={17} />}</div><small>{check.label}</small><strong>{check.value}</strong><span>{check.detail}</span><ChevronRight className="route" size={15} /></a>)}
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>EXPECTED CHAT COMMANDS</small><h3>Weekend command set</h3></div></header>
            <div className="ffs-simple-table">
              <div className="head"><span>Command</span><span>Expected state</span><span>Aliases</span></div>
              {commandRows.map((command) => {
                const seasonal = command.command === 'jump';
                const okay = seasonal ? !command.enabled : command.enabled;
                return <div className="row" key={command.command}><strong>!{command.command}</strong><span className={okay ? 'good' : 'bad'}>{command.enabled ? 'Enabled' : seasonal ? 'Disabled · seasonal' : 'Disabled · check'}</span><span>{command.aliases.length ? command.aliases.map((alias) => `!${alias}`).join(', ') : '—'}</span></div>;
              })}
            </div>
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>RECOVERY</small><h3>Failed or warning alerts</h3></div><span>{state.weekend.alerts.length}</span></header>
            {state.weekend.alerts.length === 0 ? <div className="ffs-empty-state"><CheckCircle2 size={18} /> No replayable alerts in recent history.</div> : <div className="ffs-alert-replay-list">{state.weekend.alerts.map((alert) => <div className="ffs-alert-replay-row" key={alert.id}><div><strong>{alertType(alert.type)} · {alert.userName ?? 'Someone'}</strong><span>{dateTime(alert.createdAt)} · {alert.status}</span><small>{alert.errorMessage ?? 'Warning recorded'}</small></div><button type="button" className="ffs-button secondary" disabled={busy || !state.effects.activeSession} onClick={() => void replay(alert.id, `${alertType(alert.type)} for ${alert.userName ?? 'Someone'}`)}><RotateCw size={14} /> Replay</button></div>)}</div>}
            {!state.effects.activeSession && state.weekend.alerts.length ? <p className="ffs-workspace-note">Start the stream session before replaying an alert.</p> : null}
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>WHATSAMUSIC</small><h3>Current room</h3></div><strong className={state.weekend.room.fresh ? 'ffs-status good' : 'ffs-status warning'}>{state.weekend.room.fresh ? 'LIVE' : 'NOT DETECTED'}</strong></header>
            <div className="ffs-room-state"><div><strong>{state.weekend.room.roomUrl ?? 'No fresh room URL is being reported.'}</strong>{roomUrl ? <a className="ffs-button secondary" href={roomUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> {state.weekend.room.fresh ? 'Open Current Room' : 'Open Last Reported Room'}</a> : null}</div><span>{state.weekend.room.lastSeenAt ? `Last report ${dateTime(state.weekend.room.lastSeenAt)} · considered stale after ${state.weekend.room.staleAfterSeconds}s` : 'Open the active room in Chrome so the helper can report it.'}</span></div>
          </section>
        </>
      ) : null}
    </div>
  );
}
